import { defineRouting } from "next-intl/routing";

/**
 * Shared i18n routing — 6 owlcat locales, default `ru` (matches portal/accounts
 * so the shared owlcat-locale cookie round-trips). `localePrefix: 'always'` pins
 * every route under `/<locale>/…` (the proxy middleware redirects `/`).
 */
export const locales = ["ru", "en", "cs", "uk", "de", "fr"] as const;
export const defaultLocale = "ru" as const;
export type Locale = (typeof locales)[number];

/** Native language names — for a language switcher UI. */
export const localeNames: Record<Locale, string> = {
  ru: "Русский",
  en: "English",
  cs: "Čeština",
  uk: "Українська",
  de: "Deutsch",
  fr: "Français",
};

/**
 * Cross-app locale bridge (READ side). next-intl's default locale cookie is
 * `NEXT_LOCALE`; we repoint it at the SHARED `owlcat-locale` cookie — written on
 * the parent domain by @owlcat/theme's ThemeCookieSync + the landing language
 * switcher — so a language a visitor picked on ANY owlcat app carries into this
 * one. On the locale-negotiation path (a request to `/` with no `/<locale>`
 * prefix) the middleware resolves: (1) `owlcat-locale` cookie, clamped to
 * `locales` (an unsupported value like a future 7th locale is ignored, never a
 * crash), (2) `Accept-Language`, (3) `defaultLocale`. Attributes mirror the
 * parent-domain write so next-intl's own cookie sync stays in sync instead of
 * shadowing it with a host-only cookie.
 */
const parentDomain = process.env.NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN?.trim().replace(/^\./, "");

export const routing = defineRouting({
  locales,
  defaultLocale,
  localePrefix: "always",
  localeCookie: {
    name: "owlcat-locale",
    sameSite: "lax",
    maxAge: 60 * 60 * 24 * 365,
    path: "/",
    ...(parentDomain ? { domain: `.${parentDomain}`, secure: true } : {}),
  },
});
